import { useEffect } from 'react';

import LoginView from './auth/LoginView';
import SignupView from './auth/SignupView';
import ResetView from './auth/ResetView';
import SetPasswordView from './auth/SetPasswordView';

/**
 * Slide-in panel hosting the auth views.
 *
 * Props:
 *   view      — 'login' | 'signup' | 'reset' | 'set-password' | null (null = closed)
 *   onView    — (view|null) => void, switch the active view / close
 *   resetLink — { uid, token } from a password reset email link, or null
 */
export default function SidePanel({ view, onView, resetLink }) {
  const open = view !== null;
  const close = () => onView(null);

  // Close on Escape while open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const renderView = () => {
    switch (view) {
      case 'signup':
        return <SignupView onSwitch={onView} onSuccess={close} />;
      case 'reset':
        return <ResetView onSwitch={onView} />;
      case 'set-password':
        return <SetPasswordView onSwitch={onView} resetLink={resetLink} />;
      case 'login':
      default:
        return <LoginView onSwitch={onView} onSuccess={close} />;
    }
  };

  return (
    <>
      {open && <div className="panel-overlay" onClick={close} />}
      <div className={`side-panel${open ? ' open' : ''}`} aria-hidden={!open}>
        <button className="panel-close" aria-label="Close" onClick={close}>
          &times;
        </button>
        {/* Keep the view mounted only while open so form state resets between sessions. */}
        {open && renderView()}
      </div>
    </>
  );
}
